import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Truck, BadgeDollarSign, HeartHandshake, MapPin, Phone, ArrowRight } from "lucide-react";

const features = [
  { title: "Clean & Inspected Equipment", description: "Our inflatable units are cleaned and inspected before and after every rental.", icon: ShieldCheck },
  { title: "On-Time Delivery", description: "We prioritize dependable delivery and professional setup.", icon: Truck },
  { title: "Transparent Pricing", description: "Affordable rentals with no hidden surprises.", icon: BadgeDollarSign },
  { title: "Friendly Local Service", description: "We are here to answer your questions and help you prepare for your celebration.", icon: HeartHandshake },
];

export default function AboutUs() {
  useEffect(() => {
    const oldTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const oldDescription = meta?.getAttribute("content");
    const canonical = document.querySelector('link[rel="canonical"]');
    const oldCanonical = canonical?.getAttribute("href");
    document.title = "About Us | Water Slides Houston Texas";
    meta?.setAttribute("content", "Learn about Water Slides Houston Texas, our inflatable water slide rentals, party equipment, professional setup, and service throughout Houston.");
    canonical?.setAttribute("href", "https://waterslideshoustontx.com/about-us");
    return () => {
      document.title = oldTitle;
      if (meta && oldDescription !== null && oldDescription !== undefined) meta.setAttribute("content", oldDescription);
      if (canonical && oldCanonical !== null && oldCanonical !== undefined) canonical.setAttribute("href", oldCanonical);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <section className="bg-gradient-to-br from-primary/15 via-background to-secondary/25 py-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-primary font-semibold mb-5">Fun Starts Here!</p>
          <h1 className="font-extrabold text-4xl sm:text-5xl md:text-6xl mb-5">About Water Slides Houston Texas</h1>
          <p className="text-xl sm:text-2xl text-muted-foreground">Bringing Fun to Houston, One Celebration at a Time!</p>
        </div>
      </section>
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-6 text-lg leading-relaxed text-foreground/85">
          <p>At <strong>Water Slides Houston Texas</strong>, we believe every celebration deserves a little extra excitement!</p>
          <p>We're a locally owned and operated party rental business proudly serving Houston, Texas, and surrounding communities. We specialize in inflatable water slide rentals and party equipment that help turn backyard birthdays, family gatherings, school events, church celebrations, and corporate parties into memorable experiences.</p>
          <p>We understand the Texas heat, and there's no better way to enjoy a warm Houston day than with a refreshing water slide and the people you love.</p>
        </div>
      </section>
      <section className="py-16 px-6 bg-secondary/15">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center mb-4">What We Offer</h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-10">We make planning your celebration simple and convenient.</p>
          <div className="grid md:grid-cols-3 gap-5">
            <div className="rounded-2xl bg-card border border-border/50 p-7 shadow-sm"><h3 className="text-xl font-bold mb-3">Inflatable Water Slides</h3><p className="text-muted-foreground">Exciting inflatable water slides for different ages, party sizes, and budgets.</p></div>
            <div className="rounded-2xl bg-card border border-border/50 p-7 shadow-sm"><h3 className="text-xl font-bold mb-3">Party Rentals</h3><p className="text-muted-foreground">Tables, chairs, and additional rental equipment to help prepare for your celebration.</p></div>
            <div className="rounded-2xl bg-card border border-border/50 p-7 shadow-sm"><h3 className="text-xl font-bold mb-3">Delivery & Setup</h3><p className="text-muted-foreground">We handle delivery and equipment setup so you can focus on your event.</p></div>
          </div>
          <div className="text-center mt-9"><Link to="/browse-rentals" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-bold px-7 py-3 hover:opacity-90">Browse Rentals <ArrowRight className="w-4 h-4"/></Link></div>
        </div>
      </section>
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center mb-10">Why Choose Water Slides Houston Texas?</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {features.map(({title, description, icon: Icon}) => (
              <div key={title} className="flex items-start gap-4 rounded-2xl bg-card border border-border/50 p-6">
                <div className="rounded-xl bg-primary/10 p-3 text-primary"><Icon className="w-6 h-6"/></div>
                <div><h3 className="font-bold text-lg mb-2">{title}</h3><p className="text-muted-foreground">{description}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 px-6 bg-secondary/15">
        <div className="max-w-4xl mx-auto text-center">
          <MapPin className="w-9 h-9 text-primary mx-auto mb-4"/>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-5">Proudly Serving Houston and Surrounding Communities</h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-5">We serve Houston and nearby communities including Katy, Cypress, Spring, The Woodlands, Sugar Land, Pearland, League City, Humble, Kingwood, and more.</p>
          <p className="text-lg text-muted-foreground mb-7">Free delivery is offered within 15 miles of Houston. Additional delivery fees may apply outside that area.</p>
          <Link to="/service-area" className="inline-flex items-center gap-2 font-bold text-primary hover:underline">Check Our Service Area <ArrowRight className="w-4 h-4"/></Link>
        </div>
      </section>
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-5">Our Commitment to Your Celebration</h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-7">Every event is different, but our priorities remain the same: clean equipment, dependable service, clear pricing, and a straightforward rental experience. Whether you're organizing a small backyard birthday party or a larger community gathering, we're here to help you bring the fun to your event.</p>
          <h3 className="text-2xl font-bold mb-5">Ready to Make a Splash?</h3>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/browse-rentals" className="rounded-full bg-primary text-primary-foreground font-bold px-7 py-3">Check Availability</Link>
            <a href="tel:18329704583" className="rounded-full border border-primary text-primary font-bold px-7 py-3 inline-flex items-center gap-2"><Phone className="w-4 h-4"/> (832) 970-4583</a>
          </div>
        </div>
      </section>
    </div>
  );
}
